import { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { TerminalShell } from "@/components/TerminalShell";
import { useLanguage } from "@/context/LanguageContext";
import { documentService } from "@/services/documentService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate } from "@/lib/format";

const STATUS_TONE = { pending: "text-[#F59E0B]", approved: "text-[#10B981]", rejected: "text-[#EF4444]" };
const KINDS = ["id_card", "passport", "address_proof", "other"];

export default function DocumentsPage() {
  const { t } = useLanguage();
  const [kind, setKind] = useState("id_card");
  const [number, setNumber] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    documentService.mine().then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!number.trim()) {
      toast.error("Indique o número do documento");
      return;
    }
    setBusy(true);
    try {
      await documentService.declare({ kind, document_number: number.trim(), note });
      toast.success(t("docs.sent"));
      setNumber("");
      setNote("");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TerminalShell>
      <div data-testid="documents-page" className="p-4 sm:p-8">
        <div className="max-w-2xl space-y-6">
          <div>
            <h1 className="font-heading text-2xl font-light tracking-tight text-white">{t("docs.title")}</h1>
            <p className="mt-1 text-sm text-[#8A97AD]">
              Registe os dados do seu documento. Por motivos de segurança, esta plataforma não aceita
              anexos de ficheiros — apenas os dados são guardados.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5 rounded-md border border-[#141A26] bg-[#0A0E16] p-6">
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">{t("docs.type")}</p>
              <select
                data-testid="document-kind-select"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="h-11 w-full rounded-md border border-[#1D2637] bg-[#0E1523] px-3 text-sm text-white outline-none focus:border-[#2563EB]"
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>{t(`docs.kind.${k}`)}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">Número do documento</p>
              <input
                data-testid="document-number-input"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                maxLength={60}
                className="h-11 w-full rounded-md border border-[#1D2637] bg-[#0E1523] px-3 text-sm text-white outline-none transition-colors placeholder:text-[#4A5567] focus:border-[#2563EB]"
              />
            </div>

            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">{t("docs.describe")}</p>
              <textarea
                data-testid="document-note-input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                className="w-full rounded-md border border-[#1D2637] bg-[#0E1523] p-3 text-sm text-white outline-none transition-colors placeholder:text-[#4A5567] focus:border-[#2563EB]"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="submit-document-button"
              className="inline-flex items-center gap-2 rounded-md bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("docs.send")}
            </button>
          </form>

          <section className="rounded-md border border-[#141A26] bg-[#0A0E16] p-6" data-testid="my-documents">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#5B9CFF]">{t("docs.mine")}</p>
            <div className="mt-4 space-y-3">
              {items.map((d) => (
                <div key={d.id} data-testid={`document-row-${d.id}`}
                     className="flex flex-wrap items-center justify-between gap-2 border-t border-[#141A26] pt-3 first:border-t-0 first:pt-0">
                  <div>
                    <p className="text-sm text-white">{d.document_number || "—"}</p>
                    <p className="font-mono text-[10px] text-[#5C6B82]">
                      {t(`docs.kind.${d.kind}`)} · {fmtDate(d.created_at)}
                    </p>
                    {d.review_note ? <p className="mt-1 text-xs text-[#8A97AD]">{d.review_note}</p> : null}
                  </div>
                  <span className={`font-mono text-xs uppercase ${STATUS_TONE[d.status]}`}>
                    {t(`status.${d.status}`)}
                  </span>
                </div>
              ))}
              {items.length === 0 ? (
                <p className="py-4 text-sm text-[#5C6B82]" data-testid="my-documents-empty">{t("docs.empty")}</p>
              ) : null}
            </div>
          </section>

          <div data-testid="documents-info" className="rounded-md border border-[#141A26] bg-[#0A0E16] p-6">
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#5B9CFF]">
              <FileText className="h-3.5 w-3.5" /> {t("docs.accepted")}
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#8A97AD]">
              <li>{t("docs.id")}</li>
              <li>{t("docs.passport")}</li>
              <li>{t("docs.residence")}</li>
            </ul>
          </div>
        </div>
      </div>
    </TerminalShell>
  );
}
